import {
    DisplayDocument,
    DisplayField,
    DisplaySection,
    DocumentFieldType,
    DocumentResponseSources,
    FormattingOption,
} from "@/common/utils/types";
import { Descendant, Editor, Element, Text, Transforms } from "slate";
import { HistoryEditor } from "slate-history";
import { ReactEditor } from "slate-react";
import { IS_NODE_MAP_DIRTY } from "slate-dom";
import { toast } from "react-toastify";
import {
    CustomEditor,
    CustomElement,
    CustomText,
    CustomTextDecoration,
    FieldElement,
    FieldTitleElement,
    HiddenFieldElement,
    HiddenSectionElement,
    ListElement,
    ListItemElement,
    ParagraphElement,
    SectionElement,
    TitleElement,
} from "./custom-types";
import { cloneDeep } from "lodash";
import * as Sentry from "@sentry/react";
import { groupMonitoringRows, formatMonitoringCellValue } from "@/common/utils/monitoring";

function getNodeTextDecoration(node: ChildNode): CustomTextDecoration {
    const formatting: CustomTextDecoration = {};

    if (node.nodeName === "B") formatting.bold = true;
    if (node.nodeName === "I") formatting.italic = true;
    // <ins> and <del> are added by the diffiing algorithm to show pending changes
    if (node.nodeName === "INS") formatting.highlight = true;
    if (node.nodeName === "DEL") formatting.hide = true;

    return formatting;
}

/**
 * Parses a section or field value with HTML formatting into a list of formatted text objects.
 * 'Not <b>mentioned</b> during appointment' -> [{ text: 'Not ' }, { text: 'mentioned', bold: true }, { text: ' during appointment' }]
 * @param value section or field value
 * @param isBold whether the value should display as bold
 */
export function parseFormattedText(value: string, isBold: boolean = false): CustomText[] {
    const parser = new DOMParser();
    const doc = parser.parseFromString(value, "text/html");
    const nodes: CustomText[] = [];

    doc.body.childNodes.forEach((node) => {
        if (node.nodeType === Node.TEXT_NODE) {
            // if not normal, make it bold
            if (isBold) {
                nodes.push({ text: node.textContent || "", bold: true });
            } else {
                nodes.push({ text: node.textContent || "" });
            }
        } else if (node.nodeType === Node.ELEMENT_NODE) {
            // if node is <br>, add a new line
            if (node.nodeName === "BR") {
                nodes.push({ text: "\n" });
                return;
            }

            const text = node.textContent || "";
            let textDecoration: CustomTextDecoration = getNodeTextDecoration(node);

            if (isBold) {
                textDecoration.bold = true;
            }

            // if there are children, get extra formatting from the first child
            // this allows for <b><i>text</i></b> to be bold and italic
            if (node.childNodes.length > 0) {
                textDecoration = {
                    ...textDecoration,
                    ...getNodeTextDecoration(node.childNodes[0]),
                };
            }

            nodes.push({ text, ...textDecoration });
        }
    });

    if (nodes.length === 0) {
        nodes.push({ text: "" });
    }

    return nodes;
}

/**
 * Markdown to HTML conversion
 * **string** -> <b>string</b>
 */
function replaceMarkdownBoldWithHtml(input: string): string {
    return input.replace(/\*\*(.*?)\*\*/g, "<b>$1</b>");
}

/**
 * Split on \n\n for sections
 * @param document legacy document (string)
 */
function parseLegacyDocumentToSlateObject(document: string, sectionsAndFieldsToBold: Set<string>): Descendant[] {
    const sections = document.trim().split("\n\n");

    return sections.map((section, index) => {
        section = replaceMarkdownBoldWithHtml(section);
        const lines = section.split("\n");

        // yeesh
        // if the first line is not in the set, don't bold it
        let sectionName = lines[0];
        let sectionValue = lines.slice(1).join("\n");

        if (!sectionsAndFieldsToBold.has(sectionName.toLowerCase().replace(/:/g, "").trim())) {
            sectionName = "";
            sectionValue = section;
        }

        const children = [];
        if (sectionName)
            children.push({
                type: "title",
                children: parseFormattedText(sectionName, true),
            });

        children.push({
            type: "paragraph",
            children: parseFormattedText(sectionValue),
        });

        return {
            type: "section",
            id: "",
            index,
            name: sectionName,
            children: children,
        } as Descendant;
    });
}

/**
 * Converts a display document into a Slate object.
 * @param document display document
 */
export function toSlateObject(
    document: DisplayDocument | string | null | undefined,
    sectionsAndFieldsToBold: Set<string>,
): Descendant[] {
    if (!document) {
        return [];
    }

    // STRING DOC
    if (typeof document === "string") {
        return parseLegacyDocumentToSlateObject(document, sectionsAndFieldsToBold);
    }

    // Handle plain text by handling duplicate ids
    const docCopy = cloneDeep(document);
    document = handleDuplicateSections(docCopy) as DisplayDocument;

    // JSON DOC
    const sections: Descendant[] = [];
    document.forEach((section, i) => {
        const children = [];
        if (!section || section?.isExport) return;

        // check if section has errors
        if (section.hasErrors) {
            sections.push({
                type: "section",
                id: section.id,
                index: i,
                name: section.name,
                children: [
                    {
                        type: "paragraph",
                        children: [{ text: "There was an error with this section. Please try again.", italic: true }],
                    },
                ],
            });

            return;
        }

        // put section title in the children
        if (section.name)
            children.push({
                type: "title",
                children: parseFormattedText(section.name, true),
            });

        // Monitoring grid sections carry their content in `grid`, not `value` — render
        // a single void grid node and skip the normal value/fields rendering below.
        if (section.formatting === FormattingOption.Grid && section.grid) {
            sections.push({
                type: section.isHidden ? "hidden-section" : "section",
                id: section.id,
                index: i,
                name: section.name,
                children: [...children, { type: "grid", id: section.id, grid: section.grid, children: [{ text: "" }] }],
            } as Descendant);
            return;
        }

        // Add section value as a paragraph or list
        if (section.value) {
            if (Array.isArray(section.value)) {
                if (section.value.length > 0) {
                    // Handle list values
                    children.push({
                        type: section.formatting,
                        children: section.value.map((item) => ({
                            type: "list-item",
                            children: parseFormattedText(item, section.isBold ?? !section.isNormal),
                        })),
                    });
                } else {
                    // Handle empty list
                    children.push({
                        type: "paragraph",
                        children: [{ text: "" }],
                    });
                }
            } else {
                // Handle paragraph values
                children.push({
                    type: "paragraph",
                    children: parseFormattedText(section.value, section.isBold ?? !section.isNormal),
                });
            }
        }

        // Add fields as separate blocks
        if (section.fields && section.fields.length > 0) {
            section.fields.forEach((field) => {
                const fieldChildren = [];

                if (field?.isExport) return;

                fieldChildren.push({
                    type: "field-title",
                    children: parseFormattedText(field.name, !!field.boldTitle),
                });

                if (field.value) {
                    if (Array.isArray(field.value)) {
                        if (field.value.length > 0) {
                            // list values
                            fieldChildren.push({
                                type: field.formatting,
                                children: field.value.map((item) => ({
                                    type: "list-item",
                                    children: parseFormattedText(item, field.isBold ?? !field.isNormal),
                                })),
                            });
                        } else {
                            // empty list
                            fieldChildren.push({
                                type: "paragraph",
                                children: [{ text: "" }],
                            });
                        }
                    } else {
                        // paragraph values
                        fieldChildren.push({
                            type: "paragraph",
                            children: parseFormattedText(field.value, field.isBold ?? !field.isNormal),
                        });
                    }
                } else {
                    // empty value
                    fieldChildren.push({
                        type: "paragraph",
                        children: [{ text: "" }],
                    });
                }

                // add field to children
                children.push({
                    type: field.isHidden ? "hidden-field" : "field",
                    id: field.id,
                    name: field.name,
                    children: fieldChildren,
                });
            });
        }

        if (children.length === 0) {
            children.push({
                type: "paragraph",
                children: [{ text: "" }],
            });
        }

        sections.push({
            type: section.isHidden ? "hidden-section" : "section",
            id: section.id,
            index: i,
            name: section.name,
            children,
        } as Descendant);
    });

    return sections;
}

/**
 * Replaces the entire editor document with `newValue`.
 *
 * Must go through `editor.apply` (via `Transforms`/`Editor`) rather than a raw
 * `editor.children = newValue` assignment. A raw assignment skips the DOM/node
 * bookkeeping slate-dom performs inside `apply` (it marks the node map dirty),
 * which is what tells slate-react's `onDOMBeforeInput` handler to hold off
 * resolving DOM positions against a document that changed out from under it.
 * Without that, a `beforeinput` event that was already in flight when this ran
 * (e.g. the user typing while streamed/synced content lands) throws
 * "Cannot resolve a Slate point from DOM point" - see Sentry HAPPYDOC-2FR.
 *
 * Wrapped in `HistoryEditor.withoutSaving` because these resets are
 * programmatic (loading a document, streamed/synced content landing,
 * accepting a pending AI suggestion), not a user edit - without it, `apply`
 * pushes the whole swap onto the undo stack, and Ctrl+Z could revert the
 * editor to stale content that no longer matches the saved/redux state.
 *
 * Returns whether any node operations were applied. The reset never diffs: it
 * removes every current child and inserts every new node, so this is false only
 * for an empty-to-empty reset, when no change batch flushes afterward (EN-1309).
 * EN-1253 callers ignore it: they record the resulting `editor.children` in
 * `programmaticChildrenRef` instead of arming a one-shot flag.
 */
export function resetEditorChildren(editor: CustomEditor, newValue: Descendant[]): boolean {
    const appliesNodeOperations = editor.children.length > 0 || newValue.length > 0;
    HistoryEditor.withoutSaving(editor, () => {
        Editor.withoutNormalizing(editor, () => {
            const { children } = editor;
            for (let i = children.length - 1; i >= 0; i--) {
                editor.apply({ type: "remove_node", path: [i], node: children[i] });
            }
            newValue.forEach((node, i) => {
                editor.apply({ type: "insert_node", path: [i], node });
            });
        });
        Transforms.deselect(editor);
    });
    return appliesNodeOperations;
}

/**
 * Defense-in-depth for Sentry HAPPYDOC-2FR ("Cannot resolve a Slate point from
 * DOM point"). `resetEditorChildren` above only arms slate-dom's node-map
 * "dirty" guard for resets that go through `editor.apply` - it can't help when
 * a DOM node under the caret was never touched by Slate at all (a browser
 * spellcheck/autocomplete replacement, a drag-and-drop, an extension mutating
 * contenteditable directly). slate-react's own `onDOMBeforeInput` resolves the
 * native `beforeinput` target range with `suppressThrow: false` and no way to
 * opt out, so an untracked node under the caret still crashes uncaught - a raw
 * `addEventListener`, not a React synthetic event, so React error boundaries
 * never see it. Note the crash happens BEFORE slate-react's own handler would
 * call `event.preventDefault()`, so without this guard the browser's native
 * contenteditable insertion proceeds anyway - the character silently lands in
 * the raw DOM without Slate's model (`editor.children`) ever being updated to
 * match, which is a worse failure mode than a dropped keystroke (it *looks*
 * captured on screen but isn't reflected in what gets saved).
 *
 * This attaches a CAPTURE-phase `beforeinput` listener on `container`, an
 * ancestor of slate-react's own editable DOM node, so it always runs first for
 * the same event. It mirrors the exact preconditions slate-react's own
 * `onDOMBeforeInput` uses before it would attempt this same resolution itself
 * (skip while composing, skip for plain `delete*` input types, skip while the
 * node map is already dirty - see slate-react/dist/index.es.js around lines
 * 2818, 2873), then re-does that resolution with `suppressThrow: true` to peek
 * at whether it would fail. If so, it drops just this one native event
 * (preventDefault + stopImmediatePropagation, so slate-react's own listener
 * never runs and never throws), tells the user via toast that the keystroke
 * wasn't captured (deduped via `toastId` since Sentry shows this tends to fire
 * in same-second bursts), and sets `IS_NODE_MAP_DIRTY` itself so a fast next
 * keystroke also defers instead of hitting the same desynced DOM again.
 * slate-react normally clears that flag on its own next render (`useChildren`)
 * - as a bounded fallback in case no render happens to do that (e.g. an
 * otherwise-idle editor), one `requestAnimationFrame` later this forces one
 * via `editor.onChange()` if the flag is still set. Deliberately does NOT
 * reuse `resetEditorChildren` for recovery: that swaps `editor.children` and
 * deselects, which would wipe the user's cursor and spuriously mark the
 * document dirty/autosaving for a case where nothing about the actual content
 * changed.
 *
 * `IS_NODE_MAP_DIRTY`/`ReactEditor.isComposing`/`ReactEditor.toSlateRange`'s
 * `suppressThrow` option are undocumented slate-dom/slate-react internals, not
 * public API - a version bump could change their shape. The whole precondition
 * check is wrapped in try/catch and fails OPEN (does nothing, letting the
 * event proceed exactly as if this guard weren't installed) if any of them
 * throw unexpectedly, reporting that distinctly to Sentry so a breaking
 * upstream change surfaces instead of silently mis-behaving.
 *
 * Returns a cleanup function to remove the listener.
 */
export function installBeforeInputGuard(editor: CustomEditor, container: HTMLElement): () => void {
    const handler = (event: InputEvent) => {
        try {
            if (!ReactEditor.hasEditableTarget(editor, event.target)) return;
            if (IS_NODE_MAP_DIRTY.get(editor)) return;

            const { inputType: type } = event;
            const isCompositionChange = type === "insertCompositionText" || type === "deleteCompositionText";
            if (isCompositionChange && ReactEditor.isComposing(editor)) return;
            if (type.startsWith("delete") && !type.startsWith("deleteBy")) return;

            const [targetRange] = event.getTargetRanges?.() ?? [];
            if (!targetRange) return;

            const range = ReactEditor.toSlateRange(editor, targetRange, {
                exactMatch: false,
                suppressThrow: true,
            });
            if (range) return;

            event.preventDefault();
            event.stopImmediatePropagation();
            Sentry.captureException(new Error("Suppressed Slate DOM point desync (HAPPYDOC-2FR)"), {
                extra: { inputType: type },
            });
            toast.error("A keystroke wasn't captured - please check your last few words.", {
                toastId: "slate-beforeinput-guard",
            });

            IS_NODE_MAP_DIRTY.set(editor, true);
            requestAnimationFrame(() => {
                if (IS_NODE_MAP_DIRTY.get(editor)) editor.onChange();
            });
        } catch (error) {
            Sentry.captureException(error, {
                extra: { context: "installBeforeInputGuard - unexpected error, failing open" },
            });
        }
    };

    container.addEventListener("beforeinput", handler as EventListener, { capture: true });
    return () => container.removeEventListener("beforeinput", handler as EventListener, { capture: true });
}

/**
 * Parses a paragraph element into a string. Saves as HTML.
 * @param element paragraph element
 */
function parseParagraph(
    element: ParagraphElement | TitleElement | FieldTitleElement | ListItemElement,
    trimBold: boolean = false,
): string {
    return element.children
        .map((child: CustomText) => {
            let toReturn = child.text;
            if (child.bold && !(trimBold && element.children.length === 1)) {
                // if trimBold is true and there is only one child, don't add bold tags
                toReturn = `<b>${toReturn}</b>`;
            }
            if (child.italic) {
                toReturn = `<i>${toReturn}</i>`;
            }

            return toReturn;
        })
        .join("");
}

/**
 * Parses a paragraph element into a string. Saves as HTML.
 * @param element paragraph element
 */
function parseParagraphNoTags(element: ParagraphElement | TitleElement | FieldTitleElement): string {
    return element.children
        .map((child: any) => {
            const toReturn = child.text;
            return toReturn;
        })
        .join("");
}

/**
 * Parses a list element into an array of strings. Saves as HTML.
 * @param element list element
 * @param trimBold whether to trim bold tags from the list items
 */
function parseList(element: ListElement, trimBold: boolean = false): string[] {
    return element.children.map((item: ListItemElement) => parseParagraph(item, trimBold));
}

/**
 * True when every non-empty value text leaf under the element (paragraphs, lists and list items; not its titles,
 * nested fields or grids) is bold.
 * Elements with no such leaves are treated as bold so an empty value never flips isBold.
 */
function valueLeavesAllBold(element: CustomElement): boolean {
    const skip = ["title", "field-title", "field", "hidden-field", "section", "hidden-section", "grid"];
    const leaves: CustomText[] = [];
    const collect = (nodes: Descendant[]) =>
        nodes.forEach((n: any) => {
            if (typeof n.text === "string") leaves.push(n);
            else if (!skip.includes(n.type)) collect(n.children ?? []);
        });
    collect((element.children ?? []) as Descendant[]);
    const relevant = leaves.filter((l) => l.text.trim() !== "");
    return relevant.every((l) => !!l.bold);
}

/**
 * Whether bold tags are trimmed from the element's value: true only when the Slate node's own isBold flag is set
 * (set by the bold toggle, not by load) and all value text is bold (see applyChanges). The guard in applyChanges
 * clears a flag that fails the second check, so trimming never strips tags from leaves that stay bold.
 */
function savedIsBold(element: CustomElement & { isBold?: boolean }): boolean {
    return !!element.isBold && valueLeavesAllBold(element);
}

/**
 * Parses a field element into a string or array of strings. Saves as HTML.
 * @param element field element
 */
function parseField(field: DisplayField, element: FieldElement) {
    element.children.forEach((child: Descendant) => {
        switch (child.type) {
            case "field-title":
                field.name = parseParagraphNoTags(child as FieldTitleElement);
                break;
            case "paragraph":
                // Handle multi-line paragraphs
                const contentHTML = (child as ParagraphElement).children
                    .map(convertNodeToHTML)
                    .join("")
                    .replace(/\n/g, "<br>");
                if (savedIsBold(element) && (child as ParagraphElement).children.length === 1) {
                    // isBold encodes bold already so strip any outer bold tags
                    field.value = contentHTML.replace(/^<b>/i, "").replace(/<\/b>$/i, "");
                } else {
                    field.value = contentHTML;
                }

                // handle fmt change
                if (field.formatting !== child.type) {
                    field.formatting = child.type as FormattingOption;
                }

                break;
            case "unordered-list":
            case "ordered-list":
            case "dashed-list":
            case "flat-list":
                field.value = parseList(child as ListElement, savedIsBold(element));

                // handle fmt change
                if (field.formatting !== child.type) {
                    field.formatting = child.type as FormattingOption;
                }

                break;
        }
    });
}

interface ContentToStringOptions {
    doc: string | DisplayDocument | null | undefined;
    replaceHtmlTags?: boolean;
    filterEmptyFields?: boolean;
}

/**
 * @param options.doc document
 * @param options.replaceHtmlTags replace html tags w/ markdown equivalents? important for exports
 * @param options.filterEmptyFields filter out empty fields from the document
 * @returns stringified document
 */
export const contentToString = ({
    doc,
    replaceHtmlTags = false,
    filterEmptyFields = false,
}: ContentToStringOptions): string => {
    if (filterEmptyFields && !!doc) {
        doc = removeEmptyFields(doc);
    }

    let str = typeof doc === "string" ? doc : JSON.stringify(doc) || "";
    if (replaceHtmlTags) {
        str = stripHtmlTagsOrConvertThemToMarkdown(str, true);
    }

    return str;
};

function removeEmptyFields(doc: string | DisplayDocument): string | DisplayDocument {
    // If string, try to parse as JSON; if it fails, it's a legacy plain text document
    // Legacy documents don't have structured fields, so return as-is
    if (typeof doc === "string") {
        try {
            doc = JSON.parse(doc) as DisplayDocument;
        } catch {
            return doc;
        }
    }

    return doc.map((section: DisplaySection) => ({
        ...section,
        fields: (section.fields || []).filter(
            (field: DisplayField) => field.name && field.name.trim() !== "" && !isEmptyFieldValue(field.value),
        ),
    }));
}

function isEmptyFieldValue(value: string | string[] | undefined | null): boolean {
    if (!value) return true;
    if (Array.isArray(value)) {
        return value.length === 0 || value.every((item) => !item || item.trim() === "");
    }
    return value.trim() === "";
}

/**
 * @param section section
 * @param replaceHtmlTags replace html tags w/ markdown equivalents? important for exports
 * @returns stringified section
 */
export const sectionToString = (section: string | DisplaySection, replaceHtmlTags: boolean = false): string => {
    let str = typeof section === "string" ? section : JSON.stringify(section) || "";
    if (replaceHtmlTags) {
        str = stripHtmlTagsOrConvertThemToMarkdown(str, true);
    }

    return str;
};

function makeDisplayField(fieldNode: FieldElement): DisplayField {
    const field: DisplayField = {
        id: fieldNode.id,
        name: fieldNode.name,
        value: "",
        formatting: FormattingOption.Paragraph,
        field_type: DocumentFieldType.Text,
        fields: [],
        isNormal: fieldNode.isNormal ?? true,
        isBold: savedIsBold(fieldNode),
        response_source: DocumentResponseSources.USER_DEFINED,
    };
    parseField(field, fieldNode);
    return field;
}

function makeDisplaySection(sectionNode: SectionElement): DisplaySection {
    const section: DisplaySection = {
        id: sectionNode.id,
        name: sectionNode.name,
        value: "",
        formatting: FormattingOption.Paragraph,
        field_type: DocumentFieldType.Text,
        fields: [],
        isNormal: sectionNode.isNormal ?? true,
        isBold: savedIsBold(sectionNode),
    };
    sectionNode.children.forEach((child) => {
        const c = child as CustomElement;
        switch (c.type) {
            case "title":
                section.name = parseParagraphNoTags(c as TitleElement);
                break;
            case "paragraph":
                section.value = parseParagraph(c as ParagraphElement, savedIsBold(sectionNode));
                break;
            case "unordered-list":
            case "ordered-list":
            case "dashed-list":
            case "flat-list":
                section.value = parseList(c as ListElement, savedIsBold(sectionNode));
                if (section.formatting !== c.type) {
                    section.formatting = c.type as FormattingOption;
                }
                break;
            case "field":
                section.fields.push(makeDisplayField(c as FieldElement));
                break;
        }
    });
    return section;
}

/**
 * Applies changes from slate descendant tree to a display document (ChatCompletions).
 * @param document (readonly) display document to apply changes to
 * @param changes slate descendant tree with changes to apply
 * @returns copy of display document with changes applied
 */
export function applyChanges(
    document: Readonly<DisplayDocument> | string | null | undefined,
    changes: Descendant[],
): DisplayDocument | string | null | undefined {
    // STRING DOC
    if (typeof document === "string") {
        // legacy doc is just a bunch of sections w/ paragraphs and maybe titles too
        let editedDoc = "";
        changes.forEach((value, i) => {
            value = value as SectionElement;

            value.children.forEach((child) => {
                child = child as CustomElement;
                switch (child.type) {
                    case "title":
                        editedDoc += parseParagraphNoTags(child as TitleElement) + "\n";
                        break;
                    case "paragraph":
                        editedDoc += parseParagraph(child as ParagraphElement);
                        break;
                }
            });

            // add divider if not the last section
            if (i < changes.length - 1) {
                editedDoc += "\n\n";
            }
        });

        return editedDoc;
    }

    // JSON DOC
    let editedDoc = cloneDeep(document) as DisplayDocument;

    // account for duplicate section ids
    editedDoc = handleDuplicateSections(editedDoc) as DisplayDocument;

    // EN-1253: remove unmatched sections up front; splicing inside forEach skipped the next section
    for (let i = editedDoc.length - 1; i >= 0; i--) {
        const s = editedDoc[i];
        if (s.isExport) continue;
        const matched = changes.some((change) => (change as SectionElement | HiddenSectionElement).id === s.id);
        if (!matched) editedDoc.splice(i, 1);
    }

    editedDoc.forEach((section) => {
        const sectionNode = changes.find(
            (change) => (change as SectionElement | HiddenSectionElement).id === section.id,
        ) as (SectionElement | HiddenSectionElement) | undefined;

        if (section.isExport) {
            return;
        }

        if (!sectionNode) return;

        // rm any fields not present in the changes
        if (section.fields) {
            section.fields = section.fields.filter(
                (field) =>
                    field.isExport ||
                    sectionNode.children.some((child) => (child as FieldElement | HiddenFieldElement).id === field.id),
            );
        }

        // If FieldActions set isBold on SectionElement, update the document section
        if (typeof sectionNode.isBold !== "undefined") {
            section.isBold = sectionNode.isBold;
        }

        // Same guard as fields: a stale section isBold must not re-bold value text that is not bold
        if (section.isBold && !valueLeavesAllBold(sectionNode)) {
            section.isBold = false;
        }

        // If revertToNormal set isNormal on SectionElement, update the document section
        if (typeof sectionNode.isNormal !== "undefined") {
            section.isNormal = sectionNode.isNormal;
        }

        // set isHidden properly on the section
        section.isHidden = sectionNode.type === "hidden-section";

        // make a shallow copy of fields, splice them out as we update them
        // this is important for nested plain text especially because they have the same id
        const pendingFields = section.fields?.slice() || [];
        // orderedFields collects fields in Slate order (including user-added ones)
        const orderedFields: DisplayField[] = [];

        sectionNode.children.forEach((child) => {
            child = child as CustomElement;
            switch (child.type) {
                case "title":
                    section.name = parseParagraphNoTags(child as TitleElement);
                    break;
                case "paragraph":
                    section.value = parseParagraph(child as ParagraphElement, savedIsBold(sectionNode));

                    // handle fmt change
                    if (section.formatting !== child.type) {
                        section.formatting = child.type as FormattingOption;
                    }

                    break;
                case "unordered-list":
                case "ordered-list":
                case "dashed-list":
                case "flat-list":
                    section.value = parseList(child as ListElement, savedIsBold(sectionNode));

                    // handle fmt change
                    if (section.formatting !== child.type) {
                        section.formatting = child.type as FormattingOption;
                    }

                    break;
                case "field":
                    const field = pendingFields.find((field) => field.id === child.id);

                    if (field) {
                        parseField(field, child as FieldElement);

                        // If FieldActions set isBold on FieldElement, update the document field
                        if (typeof child.isBold !== "undefined") {
                            field.isBold = child.isBold;
                        }

                        // Save what is on screen: if some value text is not bold (e.g. plain text pasted into a
                        // bold field), a stale isBold would force all of it bold again on reload.
                        if (field.isBold && !valueLeavesAllBold(child)) {
                            field.isBold = false;
                        }

                        // If revertToNormal set isNormal on FieldElement, update the document field
                        if (typeof child.isNormal !== "undefined") {
                            field.isNormal = child.isNormal;
                        }

                        // set isHidden to false, since the field is now visible
                        field.isHidden = false;

                        // remove the field from the pending fields
                        pendingFields.splice(pendingFields.indexOf(field), 1);
                        orderedFields.push(field);
                    } else {
                        // Field was added by the user (not in original document) — create it
                        orderedFields.push(makeDisplayField(child as FieldElement));
                    }
                    break;
                case "hidden-field":
                    const hiddenField = pendingFields.find((field) => field.id === child.id);

                    if (hiddenField) {
                        hiddenField.isHidden = true;

                        // remove the field from the pending fields
                        pendingFields.splice(pendingFields.indexOf(hiddenField), 1);
                        orderedFields.push(hiddenField);
                    }
                    break;
                case "hidden-section":
                    section.isHidden = true;
                    break;
                case "grid":
                    section.grid = child.grid;
                    break;
            }
        });

        // Rebuild fields in Slate order; export-only fields not in Slate are preserved at the end
        const exportOnlyFields = (section.fields || []).filter(
            (f) => f.isExport && !orderedFields.some((o) => o.id === f.id),
        );
        section.fields = [...orderedFields, ...exportOnlyFields];
    });

    // Add sections that were created by the user (not in original document)
    const existingSectionIds = new Set(editedDoc.map((s) => s.id));
    changes.forEach((change) => {
        const sectionNode = change as SectionElement;
        if (sectionNode.type !== "section") return;
        if (existingSectionIds.has(sectionNode.id)) return;
        editedDoc.push(makeDisplaySection(sectionNode));
    });

    // rm sections not in the changes, then sort to match Slate order
    const filtered = editedDoc.filter(
        (section) => section.isExport || changes.some((change) => (change as SectionElement).id === section.id),
    );
    const changeOrder = new Map(changes.map((c, i) => [(c as SectionElement).id, i]));
    filtered.sort((a, b) => {
        const aIdx = changeOrder.get(a.id) ?? Infinity;
        const bIdx = changeOrder.get(b.id) ?? Infinity;
        return aIdx - bIdx;
    });
    return filtered;
}

/**
 * Converts bytes to a readable data unit (KB, MB, GB, etc.).
 * @param {number} bytes - The number of bytes.
 * @param {number} [decimals=2] - The number of decimal places to format to.
 * @returns {string} The formatted string with the appropriate data unit.
 */
export function formatBytes(bytes: number, decimals = 2) {
    if (bytes === 0) return "0 Bytes";

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
}

/**
 * Download file blob to the user's device.
 * @param blob - The blob to download.
 * @param filename - The name of the file.
 * @returns {void}
 */
export function downloadBlob(blob: Blob, filename: string) {
    const url = window.URL.createObjectURL(new Blob([blob]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
}

/**
 * Strips all HTML tags from a string or converts them to Markdown equivalents.
 * @param text - The text to process.
 * @param markdown_copy_enabled - Whether to convert HTML tags to Markdown equivalents.
 * @returns {string} The processed text.
 */
export function stripHtmlTagsOrConvertThemToMarkdown(text: string, markdown_copy_enabled: boolean = false) {
    const htmlToMarkdown: Record<string, string> = {
        "<b>": "**",
        "</b>": "**",
        "<i>": "*",
        "</i>": "*",
        "<u>": "_",
        "</u>": "_",
        "<s>": "~~",
        "</s>": "~~",
    };

    if (markdown_copy_enabled) {
        // Replace HTML tags with Markdown equivalents
        text = text.replace(/<\/?(b|i|u|s)>/g, (tag) => htmlToMarkdown[tag] || "");
    } else {
        // Strip all valid HTML tags
        text = text.replace(/<\/?(?:[a-zA-Z][a-zA-Z0-9]*)\b[^>]*>/g, "");
    }

    return text;
}

// Helper to escape HTML special characters
function escapeHTML(str: string): string {
    return str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

// Converts a Slate leaf node (text node) to HTML with bold/italic
function convertLeafToHTML(leaf: any): string {
    const text = escapeHTML(leaf.text);
    if (leaf.bold && leaf.italic) {
        return `<b><i>${text}</i></b>`;
    } else if (leaf.bold) {
        return `<b>${text}</b>`;
    } else if (leaf.italic) {
        return `<i>${text}</i>`;
    }
    return text;
}

// Converts a Slate leaf node (text node) to plain text with bold/italic
function convertLeafToPlainText(leaf: any): string {
    const text = leaf.text;
    if (leaf.bold && leaf.italic) {
        return `**_${text}_**`;
    } else if (leaf.bold) {
        return `**${text}**`;
    } else if (leaf.italic) {
        return `_${text}_`;
    }
    return text;
}

// Recursively convert any node to HTML
function convertNodeToHTML(node: any): string {
    // If it's a text leaf:
    if ("text" in node) {
        return convertLeafToHTML(node);
    }
    // Process based on node type
    switch (node.type) {
        // Hidden elements: don’t render anything
        case "hidden-section":
        case "hidden-field":
            return "";
        case "section": {
            // Section: wrap in a div
            let html = `<div class="section">`;
            node.children.forEach((child: any) => {
                if (child.type === "title") {
                    html += `<div>${child.children.map(convertNodeToHTML).join("").toUpperCase()}</div>`;
                } else {
                    html += convertNodeToHTML(child);
                }
            });
            html += `</div>`;
            html += `<br>`;
            return html;
        }
        case "field": {
            let html = `<div class="field">`;
            node.children.forEach((child: any) => {
                if (child.type === "field-title") {
                    const fieldTitleHTML = child.children.map(convertNodeToHTML).join("");
                    html += `<span class="field-title">${fieldTitleHTML}${fieldTitleHTML.length > 0 ? ":" : ""}</span> `;
                } else if (child.type === "paragraph") {
                    // Handle multi-line paragraphs
                    const contentHTML = child.children.map(convertNodeToHTML).join("").replace(/\n/g, "<br>");
                    // Don't render empty paragraphs
                    if (contentHTML === "") {
                        html += `<br>`;
                        return html;
                    }
                    html += `<span class="field-content">${contentHTML}</span>`;
                } else {
                    // Add a newline if is a list type
                    html += "<br>";

                    html += convertNodeToHTML(child);
                }
            });
            html += `</div>`;
            return html;
        }
        case "title": // for section titles that aren’t already wrapped
            return node.children.map(convertNodeToHTML).join("");
        case "field-title":
            return node.children.map(convertNodeToHTML).join("");
        case "paragraph":
            return `<div>${node.children.map(convertNodeToHTML).join("").replace(/\n/g, "<br>")}</div>`;
        case "dashed-list":
            let dashedListHtml = `<span class="dashed-list">`;
            node.children.forEach((child: any) => {
                dashedListHtml += `<span class="dashed-list-item">- ${convertNodeToHTML(child)}</span><br>`;
            });
            dashedListHtml += `</span>`;
            return dashedListHtml;
        case "flat-list":
            let flatListHtml = `<span class="flat-list">`;
            node.children.forEach((child: any) => {
                flatListHtml += `<span class="flat-list-item">${convertNodeToHTML(child)}</span><br>`;
            });
            flatListHtml += `</span>`;
            return flatListHtml;
        case "unordered-list":
            let listHtml = `<span class="unordered-list">`;
            node.children.forEach((child: any) => {
                listHtml += `<span>• ${convertNodeToHTML(child)}</span><br>`;
            });
            listHtml += `</span>`;
            return listHtml;
        case "ordered-list":
            let orderedListHtml = `<span class="ordered-list">`;
            node.children.forEach((child: any, i: number) => {
                orderedListHtml += `<span>${i + 1}. ${convertNodeToHTML(child)}</span><br>`;
            });
            orderedListHtml += `</span>`;
            return orderedListHtml;
        case "list-item":
            return node.children.map(convertNodeToHTML).join("");
        case "grid": {
            // Grid elements store their real data in node.grid, not node.children (which is just
            // Slate's required placeholder text node) — build the table directly from it instead
            // of falling through to the default child-walking case, which would render nothing.
            if (!node.grid) {
                return "";
            }
            const grid = node.grid;
            let html = `<table class="monitoring-grid"><thead><tr><th></th>`;
            grid.columns.forEach((column: any) => {
                const time = new Date(column.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
                html += `<th>${time}</th>`;
            });
            html += `</tr></thead><tbody>`;
            const sectionGroups = groupMonitoringRows(grid.rowDefs);
            sectionGroups.forEach((group) => {
                // group.label is a fixed constant (MONITORING_SECTION_LABELS), safe as-is, but
                // row.label/unit and cell values can be LLM-extracted (medication names, dictated
                // vitals) — unlike every other case in this function, which routes text through
                // convertLeafToHTML's escapeHTML via Slate leaf nodes, this case builds HTML
                // directly from the grid payload, so it must escape those values itself.
                html += `<tr class="monitoring-grid-section"><th colspan="${grid.columns.length + 1}">${escapeHTML(group.label)}</th></tr>`;
                group.rows.forEach((row) => {
                    const rowClass = group.isPlaceholder ? "monitoring-grid-placeholder-row" : "";
                    html += `<tr class="${rowClass}"><td class="monitoring-grid-row-label">${escapeHTML(row.label)}${row.unit ? ` (${escapeHTML(row.unit)})` : ""}</td>`;
                    grid.columns.forEach((column: any) => {
                        html += `<td>${escapeHTML(formatMonitoringCellValue(row.section, grid.cells[row.id]?.[column.id]))}</td>`;
                    });
                    html += `</tr>`;
                });
            });
            html += `</tbody></table>`;
            return html;
        }
        default:
            // Fallback: process children if available
            return node.children ? node.children.map(convertNodeToHTML).join("") : "";
    }
}

// Recursively convert any node to plain text
function convertNodeToPlainText(node: any, markDownEnabled: boolean = false): string {
    if ("text" in node) {
        if (markDownEnabled) {
            return convertLeafToPlainText(node);
        } else {
            return node.text;
        }
    }

    switch (node.type) {
        // Hidden elements: don’t render anything
        case "hidden-section":
        case "hidden-field":
            return "";
        case "section": {
            let text = "";
            node.children.forEach((child: any) => {
                text += convertNodeToPlainText(child, markDownEnabled);
            });
            return text;
        }
        case "field": {
            let text = "";
            node.children.forEach((child: any) => {
                // If the child is a field title, add a colon after it
                if (child.type === "field-title") {
                    const fieldTitleText = convertNodeToPlainText(child, markDownEnabled);
                    text += fieldTitleText;
                    if (fieldTitleText.length > 0) {
                        text += ": ";
                    }
                } else {
                    // Add a newline if is a list type
                    if (child.type !== "paragraph") {
                        text += "\n";
                    }
                    text += convertNodeToPlainText(child, markDownEnabled);
                    // Add a newline if the field is a list type and markdown is enabled (ezyvet)
                    if (child.type !== "paragraph" && markDownEnabled) {
                        text += "\n";
                    }
                }
            });
            return text;
        }
        case "title":
            // capitalize the title
            return (
                node.children.map((n: any) => convertNodeToPlainText(n, markDownEnabled).toUpperCase()).join("") + "\n"
            );
        case "field-title":
            return node.children.map((n: any) => convertNodeToPlainText(n, markDownEnabled)).join("");
        case "paragraph":
            return node.children.map((n: any) => convertNodeToPlainText(n, markDownEnabled)).join("") + "\n";
        case "unordered-list": {
            let listText = "";
            node.children.forEach((child: any) => {
                listText += `• ${convertNodeToPlainText(child, markDownEnabled)}\n`;
            });
            return listText;
        }
        case "dashed-list": {
            let listText = "";
            node.children.forEach((child: any) => {
                listText += `- ${convertNodeToPlainText(child, markDownEnabled)}\n`;
            });
            return listText;
        }
        case "ordered-list": {
            let listText = "";
            node.children.forEach((child: any, i: number) => {
                listText += `${i + 1}. ${convertNodeToPlainText(child, markDownEnabled)}\n`;
            });
            return listText;
        }
        case "flat-list": {
            let listText = "";
            node.children.forEach((child: any) => {
                listText += `${convertNodeToPlainText(child, markDownEnabled)}\n`;
            });
            return listText;
        }
        case "list-item":
            return node.children.map((n: any) => convertNodeToPlainText(n, markDownEnabled)).join("");
        default:
            return node.children
                ? node.children.map((n: any) => convertNodeToPlainText(n, markDownEnabled)).join("")
                : "";
    }
}

// Main conversion functions that work on the fragment (array of nodes)
function convertFragmentToHTML(nodes: Descendant[]): string {
    return nodes.map(convertNodeToHTML).join("");
}

function convertFragmentToPlainText(nodes: Descendant[], markDownEnabled: boolean = false): string {
    return nodes.map((n) => convertNodeToPlainText(n, markDownEnabled)).join("\n");
}

const TARGET_BLOCK_TYPES = new Set(["list-item", "paragraph"]);

const CONTAINER_TYPES = new Set(["section", "field", "unordered-list", "ordered-list", "dashed-list", "flat-list"]);

/**
 * Flattens a Slate fragment by extracting specific block-level nodes
 * (like 'paragraph', 'list-item') and discarding container nodes
 * ('section', 'field', '*-list').
 *
 * @param nodes - The array of Descendant nodes (fragment) to flatten.
 * @returns A new array containing only the extracted target block nodes.
 */
function flattenFragmentToBlockContent(nodes: Descendant[]): Element[] {
    let results: Element[] = [];

    for (const node of nodes) {
        if (Text.isText(node)) {
            // Ignore top-level text nodes in this context
            continue;
        }

        if (Element.isElement(node)) {
            if (TARGET_BLOCK_TYPES.has(node.type)) {
                // It's a target node, add it directly
                results.push(node);
            } else if (CONTAINER_TYPES.has(node.type)) {
                // It's a container, recurse into its children
                const flattenedChildren = flattenFragmentToBlockContent(node.children);

                // Add the results of the recursive call to the current results
                results = results.concat(flattenedChildren);
            }
            // Ignore other element types not explicitly targeted or containers
            // (e.g., hidden-*, field-title if not targeted)
        }
    }

    return results;
}

/**
 * Converts an array of Slate `Descendant` nodes into an encoded Base64 string.
 *
 * The process involves:
 * 1. Flattening the fragment into block content.
 * 2. Serializing the flattened content into a JSON string.
 * 3. Encoding the JSON string into UTF-8 bytes.
 * 4. Converting the UTF-8 bytes into a Latin-1 string.
 * 5. Base64-encoding the Latin-1 string.
 *
 * @param nodes - An array of Slate `Descendant` nodes representing the fragment to be encoded.
 * @returns A Base64-encoded string representing the serialized and encoded fragment.
 */
function convertFragmentToEncodedSlateFragment(nodes: Descendant[]): string {
    const fragment = flattenFragmentToBlockContent(nodes);
    const json = JSON.stringify(fragment);

    // 1) UTF-8 encode the JSON into bytes
    const utf8bytes = new TextEncoder().encode(json);

    // 2) Turn each byte into a Latin-1 “character”
    let latin1 = "";
    for (const byte of Array.from(utf8bytes)) {
        latin1 += String.fromCharCode(byte);
    }

    // 3) Base64-encode that
    return btoa(latin1);
}

/**
 * Copies the given fragment to the clipboard with both HTML and plain text formatting.
 *
 * @param {Descendant[]} fragment - The fragment to be copied.
 * @param {boolean} [copy=true] - Whether to copy the fragment to the clipboard. Defaults to true.
 * @param {boolean} [markDownEnabled=false] - Whether to convert HTML tags to Markdown equivalents. Defaults to false.
 * @returns {Object} An object containing the HTML and plain text representations of the fragment.
 * @returns {string} return.html - The HTML representation of the fragment.
 * @returns {string} return.plainText - The plain text representation of the fragment.
 */
export function copySelectionWithFormatting(
    fragment: Descendant[],
    copy: boolean = true,
    markDownEnabled: boolean = false,
): { html: string; plainText: string } {
    let htmlContent = convertFragmentToHTML(fragment);
    const plainTextContent = convertFragmentToPlainText(fragment, markDownEnabled);

    if (markDownEnabled) {
        htmlContent = stripHtmlTagsOrConvertThemToMarkdown(htmlContent, true);
    }

    if (copy) {
        // Trigger a clipboard copy with both formats
        const blobPlain = new Blob([plainTextContent], { type: "text/plain" });
        const blobHTML = new Blob([htmlContent], { type: "text/html" });

        const data = {
            "text/plain": blobPlain,
            "text/html": blobHTML,
        };

        navigator.clipboard.write([new ClipboardItem(data)]).catch((err) => Sentry.captureException(err));
    }

    return {
        html: htmlContent,
        plainText: plainTextContent,
    };
}

export function copySelectionWithEvent(
    fragment: Descendant[],
    event: React.ClipboardEvent,
    markDownEnabled: boolean = false,
): Record<string, string> {
    let htmlContent = convertFragmentToHTML(fragment);
    const plainTextContent = convertFragmentToPlainText(fragment, markDownEnabled);
    const encodedFragment = convertFragmentToEncodedSlateFragment(fragment);

    if (markDownEnabled) {
        htmlContent = stripHtmlTagsOrConvertThemToMarkdown(htmlContent, true);
    }

    const data: Record<string, string> = {
        "text/plain": plainTextContent,
        "text/html": htmlContent,

        // note: slate has a custom format for clipboard data
        // if we weren't taking over onCopy, this would happen automatically
        // we serialize the slate fragment to a base64 string and set it here
        // then, on paste slate attempts to decode it and reconstruct the document
        // this mime type is not allowed on navigator.clipboard.write, so set it direct on the event
        "application/x-slate-fragment": encodedFragment,
    };

    try {
        for (const type in data) {
            event.clipboardData.setData(type, data[type]);
        }
    } catch (error) {
        Sentry.captureException(error);
    }

    return data;
}

const DUPLICATE_ID_SEPARATOR = "_";

export const convertIdToServerFormat = (id: string | undefined): string => {
    return id?.replace(DUPLICATE_ID_SEPARATOR, "&") || id || "";
};

export const convertIdToUIFormat = (id: string | undefined): string => {
    return id?.replace("&", DUPLICATE_ID_SEPARATOR) || id || "";
};

/*
 * This function handles duplicate sections in ChatJSON
 */
export function handleDuplicateSectionsInChatJSON(chatJSON: any): any {
    const chatText = handleDuplicateSections(chatJSON.chat_text);
    return {
        ...chatJSON,
        chat_text: chatText,
    };
}

/*
 * This function handles duplicate sections by adding an index to the id of the section
 */
function handleDuplicateSections(sections: any): any {
    // check for duplicated sections, and add "&<index>" to the id with the index
    const seenIds: Record<string, number> = {};
    if (Array.isArray(sections)) {
        sections.forEach((section) => {
            if (seenIds[section.id]) {
                const id = section.id; // make a copy of the base id
                section.id = `${section.id}${DUPLICATE_ID_SEPARATOR}${seenIds[section.id]}`;
                seenIds[id] += 1; // increment the index for this id
            } else {
                seenIds[section.id] = 1; // initialize the index for the base id
            }

            // check for duplicate fields
            // TODO (n-level fields): we will need to make this recursive for n-level fields
            if (Array.isArray(section.fields)) {
                section.fields?.forEach((field: any) => {
                    if (seenIds[field.id]) {
                        const id = field.id; // make a copy of the base id
                        field.id = `${field.id}${DUPLICATE_ID_SEPARATOR}${seenIds[field.id]}`;
                        seenIds[id] += 1; // increment the index for this base field id
                    } else {
                        seenIds[field.id] = 1; // initialize the index for this field id
                    }
                });
            }
        });
    }

    return sections;
}

/*
 * This function cleans duplicate ids by removing the "&<index>" from the id.
 */
export function cleanDuplicateIdsInDisplaySections(
    sections: DisplaySection[] | null | undefined,
): DisplaySection[] | null | undefined {
    if (!sections) {
        return sections;
    }
    // Copy sections/fields so we never mutate frozen Redux/Immer snapshots.
    return sections.map((section) => ({
        ...section,
        id: cleanPlainTextID(section.id) || section.id,
        // TODO (n-level fields): we will need to make this recursive for n-level fields
        fields: Array.isArray(section.fields)
            ? section.fields.map((field) => ({
                  ...field,
                  id: cleanPlainTextID(field.id) || field.id,
              }))
            : section.fields,
    }));
}

/*
 * Cleans the ID of a plain text field by removing the part after the '&' character.
 * This occurs when there are multiple plain text fields in the document, and we change their ids to be
 * <id>&<index> to avoid collisions.
 */
export function cleanPlainTextID(id: string | undefined): string | undefined {
    // remove the first part of the id (before the &)
    const cleanedID = id?.split(DUPLICATE_ID_SEPARATOR)[0];

    // if the cleanedID is empty, return the original id
    if (!cleanedID) {
        return id;
    }

    // return the cleaned ID
    return cleanedID;
}

/*
 * Converts the ids of a display document to the UI format.
 * This is used to convert the ids of a display document to the UI format when receiving a document from the server.
 * This is because the server sends the ids in the server format, but the UI expects the ids in the UI format (because they get used as ids in css selectors).
 */
export function convertDisplayDocumentIdsToUIFormat(sections: DisplaySection[]): DisplaySection[] {
    return sections.map((section) => {
        const id = convertIdToUIFormat(section.id) || section.id;
        const fields = Array.isArray(section.fields)
            ? section.fields.map((field) => ({
                  ...field,
                  id: convertIdToUIFormat(field.id) || field.id,
              }))
            : section.fields;
        return { ...section, id, fields };
    });
}
